import * as React from "react";
import { Box, Breadcrumbs, Container } from "@mui/material";
import { messages } from "../../utils/i18n/i18n";
import { useTranslation } from "../../utils/i18n/lang";
import {
    dashboardRoute,
    organizationsLoadedCtx,
    orgRoute,
    useOrganizationState,
} from "../../routes/user";
import { array, option } from "fp-ts";
import { useRouter } from "next/router";
import { constNull, pipe } from "fp-ts/function";
import { TabLink } from "./TabLink";
import { HomeLink } from "../xathlo/HomeLink";
import Layout from "../xathlo/Layout";
import { NextSeo } from "next-seo";
import { AccountBtn } from "./AccountBtn";
import { UserSpaceSwitch } from "./UserSpaceSwitch";
import { Breakpoint } from "@mui/system";

interface Props {
    children: React.ReactNode;
    childrenMaxWidth?: Breakpoint; // sm default
    breadCrumbs?: Array<React.ReactNode>;
    submenu?: React.ReactNode;
}

export function OrgLayout(p: Props) {
    const m = useTranslation(msg);
    const organizationsState = useOrganizationState();
    const router = useRouter();

    const orgQuery = {
        org: option.toNullable(organizationsState.subdomainFromRoute),
    };

    return (
        <>
            <NextSeo noindex={true} />
            <Layout
                breadCrumbs={
                    <Breadcrumbs>
                        {pipe(
                            [
                                <HomeLink
                                    key={0}
                                    variant={"x"}
                                    href={dashboardRoute.route({})}
                                />,
                            ],
                            array.concat(
                                pipe(
                                    organizationsState,
                                    organizationsLoadedCtx,
                                    option.map((orgCtx) => (
                                        <UserSpaceSwitch
                                            key={1}
                                            route={orgRoute.index.route({
                                                org: orgCtx.active.subdomain,
                                            })}
                                            label={orgCtx.active.name}
                                        />
                                    )),
                                    array.fromOption,
                                    array.concat(
                                        p.breadCrumbs == null
                                            ? []
                                            : p.breadCrumbs
                                    )
                                )
                            )
                        )}
                    </Breadcrumbs>
                }
                rightMenu={
                    <>
                        <TabLink
                            label={m.events}
                            href={orgRoute.index.route(orgQuery)}
                            router={router}
                        />
                        <TabLink
                            label={m.news}
                            href={orgRoute.news.route(orgQuery)}
                            router={router}
                        />
                        <TabLink
                            label={m.clubs}
                            href={orgRoute.clubs.route(orgQuery)}
                            router={router}
                        />
                        <TabLink
                            label={m.athletes}
                            href={orgRoute.athletes.route(orgQuery)}
                            router={router}
                        />
                        <TabLink
                            label={m.pages}
                            href={orgRoute.pages.list.route(orgQuery)}
                            router={router}
                        />
                        <TabLink
                            label={m.settings}
                            href={orgRoute.settings.route(orgQuery)}
                            router={router}
                        />
                        <AccountBtn />
                    </>
                }
            >
                <Box
                    sx={{
                        flexGrow: 1,
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    {p.submenu == null ? null : (
                        <Container
                            maxWidth={"lg"}
                            sx={{
                                p: 1,
                                pt: 2,
                                position: "sticky",
                                top: 0,
                                zIndex: 1,
                                backgroundColor: (theme) =>
                                    theme.surfaceColor.topLevel,
                            }}
                        >
                            {p.submenu}
                        </Container>
                    )}
                    <Container
                        maxWidth={p.childrenMaxWidth || "md"}
                        sx={{
                            flexGrow: 1,
                            display: "flex",
                            flexDirection: "column",
                        }}
                    >
                        {p.children}
                    </Container>
                </Box>
            </Layout>
        </>
    );
}

const msg = messages(
    {
        events: "Events",
        news: "News",
        logout: "Logout",
        clubs: "Clubs",
        athletes: "Athletes",
        pages: "Pages",
        settings: "Settings",
    },
    {
        cs: {
            events: "Události",
            news: "Aktuality",
            logout: "Odhlásit",
            clubs: "Týmy",
            athletes: "Závodníci",
            pages: "Stránky",
            settings: "Organizace",
        },
    }
);
